import {
  CONTENT_CAPTION_ELEMENT_CLASS_NAME,
  EDITORIAL_CONTENT_NODE_ID,
  PRODUCT_DESCRIPTION_ELEMENT_CLASS_NAME
} from '../constants';
import {
  nodeAttrs,
  setLocation,
  getLinksById,
  getLinksByClassName,
  onHomepage
} from './dom';
import {
  isClickTrackerLink,
  isMailtoLink,
  isSlideLink,
  isInternalLink
} from './link';
import createMockAd from './ads/createMockAd';
import { onProductionHostname } from './env';

import format from 'date-fns/format';
import {get, last, once, find, defer, filter, isEmpty, isInteger, isFunction, forEach} from 'lodash';

export const HIT_CALLBACK_TIMEOUT = 1000;
const GA_TIMESTAMP = 'YYYY-MM-DD';
// Production Google Optimize ID uses Production GA User ID
const PRODUCTION_GA_USER_ID = 'UA-315022-1';
const PRODUCTION_GOOGLE_OPTIMIZE_ID = 'GTM-TKDD9DK';
// Development Google Optimize uses Development GA User ID
const DEVELOPMENT_GA_USER_ID = 'UA-315022-5';
const DEVELOPMENT_GOOGLE_OPTIMIZE_ID = 'GTM-NF45L7Q';
const AMP_VERSION_NON_AMP = '0';

/*
 * Google Analytics
 *
 */

export const getGATrackingId = hostname =>
  onProductionHostname(hostname)
    ? PRODUCTION_GA_USER_ID
    : DEVELOPMENT_GA_USER_ID;

export const getGoogleOptimizeId = hostname =>
  onProductionHostname(hostname)
    ? PRODUCTION_GOOGLE_OPTIMIZE_ID
    : DEVELOPMENT_GOOGLE_OPTIMIZE_ID;

export function sendAnalyticsEvent(...params) {
  gaSend('event', ...params);
}

/**
 * This pushes up an R29 Google Analytics event using dataLayer.
 *
 * You must specifiy the event's name and metadata. It optionally can take a
 * boolean which controls whether or not the event name is modified according
 * to the page template it occurred on.
 *
 * This automatically merges event-level data passed into the sendDataLayerEvent
 * call with the page-level data set on the window in updateDataLayerMetadata.
 *
 * ref: https://developers.google.com/analytics/devguides/collection/analyticsjs/command-queue-reference#send
 * ref: https://developers.google.com/analytics/devguides/collection/upgrade/analyticsjs
 * ref: https://developers.google.com/tag-manager/devguide
 */
export function sendDataLayerEvent(eventName, eventData, templateSpecificEvent = true, location) {
  const dataLayer = window._dataLayer || (window._dataLayer = []);

  if (templateSpecificEvent && window.dataLayerMetadata) {
    // Assert the page type we are on and modify event name accordingly
    if (window.dataLayerMetadata.channel === 'homepage') {
      eventName = 'Homepage | ' + eventName;
    } else if (window.dataLayerMetadata.articleUID) {
      if (!isEmpty(window.dataLayerMetadata.series)) {
        eventName = 'Video | ' + eventName;
      } else {
        eventName = 'Article | ' + eventName;
      }
    } else if (window.dataLayerMetadata.pageType === 'product_list') {
      eventName = 'Collection | ' + eventName;
    } else if (window.dataLayerMetadata.pageType === 'aggregation') {
      eventName = 'Channel | ' + eventName;
    }
  }

  // combine the event-level metadata with the global page-level metadata
  const mergedData = {
    event: eventName,
    ...eventData,
    ...window.dataLayerMetadata
  };

// Renaming the event specifically for clicks occurring on the homepage, using homepage flag
  if (mergedData.event === 'Click' && mergedData.homepage) {
    mergedData.event = 'homepage_module_click';
  }

  // Push the event to dataLayer
  dataLayer.push(mergedData);
  if(location) {
    // dataLayer hooks eventCallback and hitCallback aren't working for some reason. Others commonly report the same problem
    // Setting a timeout allows GTM to pick up the dataLayer change before changing route.
    setTimeout(() => {
      document.location = location;
    }, 2000);
  }
}

/*
 * This is called by updateBeacons and sets up the window.dataLayerMetaData
 * object. It holds all of the page-level metadata we want to pass alongside
 * every single dataLayer event that is fired on the page. We ensure that by
 * merging this data into the event-level metadata inside of sendDataLayerEvent.
 */
export function updateDataLayerMetadata(loc, state) {

  // Channel/Homepage Data

  // Adding homepage flag to dataLayerMetadata since staging and production does not recognize the homepage channel
  if (get(state, 'ads.ad_data.ad_page_type') === 'homepage') {
    window.dataLayerMetadata = {
      ...window.dataLayerMetadata,
      homepage: true,
    }
  }

  if (get(state, 'ads.ad_data.ad_page_type') === 'aggregation') {
    const channelName = onHomepage(loc, state.i18n.edition_data)
      ? 'homepage'
      : get(state, 'ads.ad_data.ad_pageid', '');
    window.dataLayerMetadata = {
      ...window.dataLayerMetadata,
      locale: get(state, 'ads.ad_data.ad_edition', ''),
      pageTitle: get(state, 'meta.meta_title', ''),
      pageType: get(state, 'ads.ad_data.ad_page_type'),
      vertical: 'Refinery29',
      channel: channelName,
      channelID: get(state, 'ads.ad_data.ad_entityid', '')
    };
  }

  // Article/Video Data
  if (!isEmpty(state.article)) {
    const shareImage = find(
      state.article.opener_images,
      { label: 'FACEBOOK' }
    );
    const bylines = get(state, 'article.bylines', []);

    let contributorsString = '';
    bylines.forEach((byline, i) => {
      contributorsString += `${byline.name}:${byline.byline}`;
      if (i !== bylines.length - 1) {
        contributorsString += ', ';
      }
    });

    const editors = [];
    bylines.forEach((byline) => {
      if (byline.featured && byline.byline === 'Written') {
        editors.push(`${byline.name}:${byline.byline}`);
      }
    });
    const editorString = editors.join(', ');

    // Read the topics from the breadcrumbs
    const topics = [];
    get(state, 'article.breadcrumbs', []).forEach((breadcrumb) => {
      if (breadcrumb.name) {
        topics.push(breadcrumb.name);
      }
    });

    window.dataLayerMetadata = {
      ...window.dataLayerMetadata,
      articleTitle: get(state.article, 'sanitizedTitle', ''),
      articleUID: get(state.article, 'id', ''),
      adChannels: get(state, 'ads.ad_data.ad_channels', []),
      adChannelsList: get(state, 'ads.ad_data.ad_channels', []).join('|'),
      contributors: contributorsString,
      campaign: get(state, 'ads.ad_data.ad_sponsorship', ''),
      channel: get(state.article, 'primary_category.slug', ''),
      channelID: get(state.article, 'primary_category.id', ''),
      editor: editorString,
      locale: get(state, 'ads.ad_data.ad_edition', ''),
      notSafeForWork: get(state.article, 'isSensitiveContent', ''),
      pageTitle: get(state, 'meta.meta_title', ''),
      pageType: get(state, 'ads.ad_data.ad_page_type'),
      publishDate: get(state.article, 'published_formatted', ''),
      series: get(state, 'series.name', ''),
      shareDescription: get(state, 'meta.meta_description', ''),
      shareImage: shareImage.asset.src.full,
      shareTitle: get(state.article, 'social_title', ''),
      sponsorName: get(state, 'ads.ad_data.ad_sponsorship', ''),
      templateType: get(state, 'analytics.page_type', ''),
      topicArray: topics,
      topicList: topics.join('|'),
      vertical: 'Refinery29',
      isSponsored: get(state, 'ads.ad_data.ad_sponsorship', '') ? 'TRUE' : 'FALSE'
    };
  }
}

export function dataLayerSendPageview(loc, state) {
  if (get(state, 'ads.ad_data.ad_page_type') === 'aggregation') {
    if (onHomepage(loc, state.i18n.edition_data)) {
      sendDataLayerEvent('homepage', {}, false);
    } else {
      sendDataLayerEvent('channel', {}, false);
    }
  } else if (!isEmpty(state.article)) {
    if (!isEmpty(state.series)) {
      sendDataLayerEvent('video', {}, false);
    } else {
      sendDataLayerEvent('article', {}, false);
    }
  }
}

export function sendSocialEvent(...params) {
  gaSend('social', ...params);
}

export function sendGAPageView(loc) {
  const { pathname, search } = loc;
  gaSet('page', pathname + search);
  gaSend('pageview');
}

export function updateGACustomVariables({ analytics, article, product, experiments }) {
  setGAContentGroup(1, analytics.page_type);

  if (!isEmpty(analytics.reporting_category)) {
    setGAContentGroup(2, analytics.reporting_category);
  }
  if (!isEmpty(article)) {
    updateGACustomVariablesForArticle(article);
  }
  if (!isEmpty(product)) {
    updateGACustomVariablesForProduct(product);
  }

  forEach(experiments, ({ customDimension, chosenVariant }) => {
    setGACustomDimension(customDimension, chosenVariant);
  });
}

function updateGACustomVariablesForArticle(article) {
  const { bylines, id, original_published } = article;
  if (isInteger(original_published)) {
    setGACustomDimension(
      4,
      format(new Date(original_published * 1000), GA_TIMESTAMP)
    );
  }
  setGACustomDimension(
    5,
    String(id)
  );
  setGACustomDimension(
    6,
    get(filter(bylines, 'featured'), '[0].name')
  );

  // non-AMP version of a story
  setGACustomDimension(19, AMP_VERSION_NON_AMP);
}

function updateGACustomVariablesForProduct(product) {
  const categoryName = get(product, 'category.name');
  const subcategoryName = get(product, 'subcategory.name');
  const className = get(product, 'class.name');

  let categoryString;
  if (categoryName) {
    categoryString = `${categoryName}`;
    if (subcategoryName) {
      categoryString = categoryString.concat(` - ${subcategoryName}`);
      if (className) {
        categoryString = categoryString.concat(` - ${className}`);
      }
    }

    setGACustomDimension(11, categoryString);
  }
}

export function setGAClientIDCustomDimension() {
  setGACustomDimension(7, tracker => tracker.get('clientId'));
}

export function setGASkimlinksCustomDimension(data) {
  if (data.guid) {
    setGACustomDimension(9, data.guid);
  }
}

export function setGAAdBlockTrackingCustomDimension(bodyNode) {
  if (!window.ga) { return; }
  const body = bodyNode || document.body;
  const ad = createMockAd();
  body.appendChild(ad);
  defer(function() {
    const { clientHeight } = nodeAttrs(ad);
    const isAdBlockEnabled = clientHeight > 0 ? '0' : '1';
    setGACustomDimension(8, isAdBlockEnabled);
    body.removeChild(ad);
  });
}

export function sendGASlideViewEvents(sectionNum) {
  setGACustomDimension(1, `${sectionNum}`);
  sendAnalyticsEvent(
    'Story Completion',
    'View Section',
    `${sectionNum}`,
    { nonInteraction: true }
  );
}

export function sendDataLayerSlideViewEvent(sectionNum) {
  sendDataLayerEvent('View', {
    actionType: 'Story Completion',
    slideshowComponent: 'Story Completion',
    pageModule: 'Slideshow',
    slideNumber: sectionNum
  });
}

type LinkEventListener = Event => void;

const handleSameTabLinkClick = (
  params: Array<string>,
  href: string,
  innerText: string
): LinkEventListener => (event: MouseEvent): void => {
  event.preventDefault();
  sendAnalyticsEvent(
    ...params, {
      nonInteraction: true,
      hitCallback: (): string => setLocation(href)
    }
  );
  if (params[0] === 'Internal Link') {
    sendDataLayerEvent('Click', {
      linkType: 'article',
      linkURL: href,
      linkTitle: innerText,
      pageModule: 'Main Content'
    });
  } else {
    if (params[1] === 'Click Tracker') {
      sendDataLayerEvent('Click', {
        linkType: 'outbound clicktracker',
        linkURL: href,
        linkTitle: innerText,
        pageModule: 'Main Content'
      });
    } else {
      sendDataLayerEvent('Click', {
        linkType: 'outbound',
        linkURL: href,
        linkTitle: innerText,
        pageModule: 'Main Content'
      });
    }
  }
};

const handleNewTabLinkClick = (
  params: Array<string>,
  href: string,
  innerText: string
): LinkEventListener =>
  (): void => {
    sendAnalyticsEvent(
      ...params,
      { nonInteraction: true }
    );

    if (params[0] === 'Internal Link') {
      sendDataLayerEvent('Click', {
        linkType: 'article',
        linkURL: href,
        linkTitle: innerText,
        pageModule: 'Main Content'
      });
    } else if (params[0] === 'Super Sale Link') {
      sendDataLayerEvent('Click', {
        linkType: 'sticky promo',
        linkURL: href,
        linkTitle: innerText,
        pageModule: 'Main Content'
      });
    } else {
      if (params[1] === 'Click Tracker') {
        sendDataLayerEvent('Click', {
          linkType: 'outbound clicktracker',
          linkURL: href,
          linkTitle: innerText,
          pageModule: 'Main Content'
        });
      } else {
        sendDataLayerEvent('Click', {
          linkType: 'outbound',
          linkURL: href,
          linkTitle: innerText,
          pageModule: 'Main Content'
        });
      }
    }
  };


const linkUAEventParams =
  (href: string, customLabel: string | null): Array<string> => {
    if (customLabel) {
      return [customLabel, 'Click', href];
    }
    if (isInternalLink(href)) {
      return ['Internal Link', 'Click', href];
    }
    if (isClickTrackerLink(href)) {
      return ['Outbound Link', 'Click Tracker', href];
    }
    return ['Outbound Link', 'Click', href];
  };

const addClickTrackingEventListener =
  (link: HTMLAnchorElement): void => {
    const { attributes, href, target, innerText } = link;
    const customLabel = attributes ? get(attributes.getNamedItem('data-event-label'), 'value') : null;

    const params = linkUAEventParams(href, customLabel);
    const eventHandler = (
      target === '_blank'
        ? handleNewTabLinkClick
        : handleSameTabLinkClick
    );
    link.addEventListener(
      'click',
      eventHandler(params, href, innerText),
      false
    );
    link.dataset.trackerAdded = true;


  };

/**
 * Do no add link tracking to internal slideshow links or mailto links
 */
const shouldAddLinkClickTracker =
  ({ href, dataset: { trackerAdded } }: HTMLAnchorElement): bool =>
    !isMailtoLink(href) && !isSlideLink(href) && !trackerAdded;

/**
 * Link finding utility methods
 */
const linksInMainEditorialContent =
  (): Array<HTMLAnchorElement> => getLinksById(
    EDITORIAL_CONTENT_NODE_ID,
    document
  );

const linksInContentCaptions =
  (): Array<HTMLAnchorElement> => getLinksByClassName(
    CONTENT_CAPTION_ELEMENT_CLASS_NAME,
    document
  );

const linksInProductDescriptions =
  (): Array<HTMLAnchorElement> => getLinksByClassName(
    PRODUCT_DESCRIPTION_ELEMENT_CLASS_NAME,
    document
  );

const linksInEditorialContent =
  (): Array<HTMLAnchorElement> => [
    ...linksInMainEditorialContent(),
    ...linksInContentCaptions(),
    ...linksInProductDescriptions()
  ];

/**
 * Using cascade of helper functions, add link tracking to all links in the
 * content of the article. We look through all body, products, and captions.
 */
export const addEditorialLinkClickTrackers = (): void => {
  for (const link of linksInEditorialContent()) {
    if (!shouldAddLinkClickTracker(link)) {
      continue;
    }
    addClickTrackingEventListener(link);
  }
};





/*
 * Helpers
 *
 */

function setGAContentGroup(groupNumber, data) {
  gaSet(`contentGroup${groupNumber}`, data);
}

function setGACustomDimension(dimensionNumber, data) {
  gaSet(`dimension${dimensionNumber}`, data);
}

/*
 * Usage:
 *   // default style
 * - gaSet('dimension1', 'foo');
 *   // readyCallback style
 * - gaSet('dimension1', tracker => tracker.getSomethingYouNeed());
 */
function gaSet(fieldName, fieldValue) {
  if (typeof window.ga !== 'function') {
    return;
  }
  /*
   * We could *always* use the readyCallback style, but it
   * makes debugging events a bit harder. Let's make QA's life easier!
   */
  const shouldUseReadyCallback = typeof fieldValue === 'function';
  if (shouldUseReadyCallback) {
    window.ga(tracker => tracker.set(fieldName, fieldValue(tracker)));
  } else {
    window.ga('set', fieldName, fieldValue);
  }
}

/*
 * Usage:
 * - gaSend('event', 'category', 'action', 'label', 1);
 * - gaSend('event', 'category', 'action', ..., {
 *     hitCallback: () => doSomethingAfterHit()
 *   });
 */
function gaSend(...params) {
  const fieldsObject = typeof last(params) === 'object' ? last(params) : {};
  if (fieldsObject.hitCallback) {
    fieldsObject.hitCallback = once(fieldsObject.hitCallback);
    window.setTimeout(fieldsObject.hitCallback, HIT_CALLBACK_TIMEOUT);
  }

  if (typeof window.ga === 'function') {
    window.ga('send', ...params);
  } else if (fieldsObject.hitCallback) {
    fieldsObject.hitCallback();
  }
}

/*
 * Timing Trackers
 *
 */

export const trackTimeToFirstByte = (window) => {
  const startTime = get(window, 'performance.timing.responseStart');
  const endTime = get(window, 'performance.timing.navigationStart');
  const timeToFirstByte = startTime - endTime;

  if (!window.ga || !timeToFirstByte) {
    return;
  }

  window.ga('send', 'timing', 'Page Performance', 'timeToFirstByte', timeToFirstByte);
};

/*
 * Tracks time elapsed from receiving the first byte
 * to displaying the first ad.
 */
export const trackTimeToFirstAd = once((window) => {
  const timeToFirstByte = get(window, 'performance.timing.responseStart');

  if (!timeToFirstByte) {
    return;
  }

  const timeToFirstAd = window.Date.now() - timeToFirstByte;

  window.ga && window.ga('send', 'timing', 'Page Performance', 'timeToFirstAd', timeToFirstAd);
});
